import { useCallback, useRef } from 'react';
import { CallBackProps, STATUS } from 'react-joyride';
import { usePostHog } from 'src/lib/posthog';

/**
 * Emits one `<name>_started` / `<name>_skipped` / `<name>_completed` event per tour run.
 *
 * react-joyride invokes its callback on *every lifecycle event of every step*, and all of
 * them carry `status: 'running'`. Capturing `started` straight off that status therefore
 * emits roughly one event per step, inflating the funnel's denominator so that
 * started:completed reads as a step count rather than a completion rate.
 *
 * A ref latch fixes this by capturing on the *transition into* a running tour. It also
 * gates the terminal events on that same latch, so `skipped`/`completed` can only be
 * counted against a run that was itself counted as started — which is what makes the two
 * numbers comparable.
 *
 * A run can also end without a terminal status: when a host flips Joyride's `run` prop to
 * false (closing a modal mid-tour, say) the store moves to `paused`, not `finished` or
 * `skipped`. That still reaches us, via the catch-all `tour:status` callback, so the latch
 * is released on any other status. Without that release, a host that stays mounted across
 * close and reopen — an always-rendered modal taking `open` as a prop — would hold the
 * latch and swallow the *next* run's `started`, turning this hook's over-count into an
 * under-count. Such an interrupted run emits `started` with no terminal event, which is
 * the honest record: it was abandoned, not skipped.
 */
const useTourAnalytics = (name: string, properties?: Record<string, unknown>) => {
  const posthog = usePostHog();
  // True between the start of a run and the point it stops running.
  const runInProgress = useRef(false);

  // Read through a ref so the returned callback stays stable even when a caller passes a
  // fresh object literal (e.g. `{ groupId }`) on every render.
  const propertiesRef = useRef(properties);
  propertiesRef.current = properties;

  // Snapshot taken when the run starts, so a run's terminal event carries the same
  // properties its `started` event did even if the caller's values change mid-tour.
  const runPropertiesRef = useRef(properties);

  return useCallback(
    (data: CallBackProps) => {
      const { status } = data;

      if (status === STATUS.RUNNING) {
        if (runInProgress.current) return;
        runInProgress.current = true;
        runPropertiesRef.current = propertiesRef.current;
        posthog?.capture(`${name}_started`, runPropertiesRef.current);
        return;
      }

      if (!runInProgress.current) return;
      runInProgress.current = false;

      if (status === STATUS.SKIPPED || status === STATUS.FINISHED) {
        const outcome = status === STATUS.SKIPPED ? 'skipped' : 'completed';
        posthog?.capture(`${name}_${outcome}`, runPropertiesRef.current);
      }
    },
    [posthog, name]
  );
};

export default useTourAnalytics;
